import { buildVisibilityAwareKeyArgs, MESSAGE_FILTER_CACHE_FIELDS } from './util/messageFilterCacheKeyArgs';
import { setAuthToken, subscribeAuthTokenChanges } from './utils/authSession';
import { AuthenticatedSubscriptionClient } from './utils/subscriptionTransport';
import { mergeSharedFolderContent } from './util/sharedFolderPagination';
import {PLAN_BENEFIT_TYPE_POLICY} from './i18n/planBenefits';
import PseudoPreviewControl from './i18n/PseudoPreviewControl';
import './i18n';
import {useTranslation} from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
// import { ApolloClient } from 'apollo-client';
import { split } from 'apollo-link';
import { getMainDefinition } from 'apollo-utilities';
// import { onError } from 'apollo-link-error';
import { onError } from '@apollo/client/link/error';
// import { createHttpLink } from 'apollo-link-http';
import { WebSocketLink } from 'apollo-link-ws';
import { ApolloProvider, InMemoryCache, ApolloClient, HttpLink } from '@apollo/client';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
// Error
import GraphqlErrorBoundary from './components/ErrorComponents/GraphqlErrorBoundary';
// Middlware and afterware
import { jwtHeadersAfterware } from './config/afterware';
import { accessTokenLink } from './config/middleware';
import Routes from './Routes';
import introspectionQueryResultData from '../src/fragmentTypes.json';
import CookieConsent from 'react-cookie-consent';
import environment from './env-config.js'
// import { TooltipProvider } from './components/Tooltip/TooltipManager';
import ReactTooltip from 'react-tooltip';
import { REDIRECT_TO_LOGIN, MAX_STORAGE_REACHED } from './errors/errors';
import { getSubscriptionConnectionParams } from './utils/authHeaders';
import { getPostLogoutRedirect } from './util/logoutRedirect';
import { clearLocalStoragePreservingOnboarding } from './util/preserveOnboardingStorage';
import { registerDesktopUpdateMenuListener, scheduleDesktopUpdateCheck } from './utils/desktopUpdater';

// APOLLO CLIENT SETUP
// 1) Instantiate an ApolloClient, and for the link property set the graphql application endpoint
// 2) Wrap the AppComponent with Apollo-Provider tag, and set the client property to the isntantiated
//    ApolloClient

// IF ERROR ON PRODUCTION, CHECK THE URL, MAYBE IT NEEDS PORT, SHOULDN'T BE THE CASE

const wsUri = environment.REACT_SUBSCRIPTION_URL;
const IS_PRERENDER =
    typeof navigator !== 'undefined' &&
    (
        navigator.userAgent === 'ReactSnap' ||
        navigator.userAgent.includes('ReactSnap') ||
        (typeof window !== 'undefined' && typeof window.snapSaveState === 'function')
    );

// Error middleware
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors)
        graphQLErrors.map(({ message, locations, path, extensions }) => {
            const pathText = Array.isArray(path) ? path.join('.') : String(path || '');
            const errorCode = extensions?.code;
            const isCreateMessageWriteConflict =
                (message?.includes('Write conflict') || message?.includes('Please retry')) &&
                (pathText.includes('createMessage') || operation?.operationName === 'createMessage');

            // createMessage write conflicts are retried by MessageInput; suppress noisy global logs
            if (isCreateMessageWriteConflict) {
                if (environment.REACT_NODE_ENV === 'development') {
                    console.log('[GraphQL retryable]: createMessage write conflict');
                }
                return;
            }

            console.log(
                `[GraphQL error]: Message: ${message}, Code: ${errorCode || 'n/a'}, Location: ${JSON.stringify(locations)}, Path: ${path}`
            );
            if (errorCode === REDIRECT_TO_LOGIN) {
                if (typeof window !== 'undefined') {
                    window.__logoutInProgress = true;
                }
                clearLocalStoragePreservingOnboarding();
                window.location.replace(getPostLogoutRedirect() || '/login');
            } else if (errorCode === MAX_STORAGE_REACHED) {
                // Storage-limit handling is done in upload UI (upgrade prompt). Never force logout here.
                return;
            } else {
                console.log('dispatch');
                // here to put snackbar
            }
        });
    
    if (networkError) {
        // Suppress WebSocket connection errors in console - these are expected if server is down
        const isWebSocketError = networkError.message && (
            networkError.message.includes('WebSocket') ||
            networkError.message.includes('wss://') ||
            networkError.message.includes('connection') ||
            networkError.name === 'NetworkError'
        );
        
        // Suppress errors related to cache clearing during logout
        const isCacheClearingError = networkError.message && (
            networkError.message.includes('Cannot read properties of undefined') ||
            networkError.message.includes("reading 'data'") ||
            networkError.message.includes('toQueryResult')
        );
        
        // Only log non-WebSocket and non-cache-clearing errors, or if in development
        if ((!isWebSocketError && !isCacheClearingError) || environment.REACT_NODE_ENV === 'development') {
            if (!isCacheClearingError) {
                console.log(`[Network error]: ${networkError}`);
            }
        }
        
        // For WebSocket errors, silently retry or fail gracefully
        if (isWebSocketError && operation.operationName !== 'subscription') {
            // For non-subscription operations, retry with HTTP
            return forward(operation);
        }
        
        // For cache clearing errors during logout, silently ignore
        if (isCacheClearingError) {
            return; // Don't forward, just ignore the error
        }
    }
});

let wsLink;

const initializeApolloClient = async () => {
    const httpLink = new HttpLink({
        uri: environment.REACT_BACKEND_URL
    });

    const httpLinkWithMiddleware = jwtHeadersAfterware.concat(accessTokenLink.concat(httpLink));

    wsLink = new WebSocketLink(new AuthenticatedSubscriptionClient(wsUri, {
        getToken: () => localStorage.getItem('token'),
        connectionParams: getSubscriptionConnectionParams
    }));

    const link = split(
        ({ query }) => {
            const definition = getMainDefinition(query);
            return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
        },
        wsLink,
        httpLinkWithMiddleware
    );

    const fragmentMatcher = new IntrospectionFragmentMatcher({
        introspectionQueryResultData
    });

    // Track recently moved message IDs to prevent them from reappearing in source folders
    // This is a global set that merge policies check before adding messages
    // Messages are added when moved and removed after 5 seconds (enough time for network fetches to complete)
    if (typeof window !== 'undefined') {
        window.__recentlyMovedMessages = window.__recentlyMovedMessages || new Map();
        // Track pagination sequence numbers to detect stale responses
        // Each folder has a sequence number that increments when fetchMore is called
        // If incoming data has a lower sequence number, it's from a stale request
        window.__paginationSequence = window.__paginationSequence || new Map();
    }
    
    // Type policies for proper cache normalization
    // This ensures messages are normalized by ID and updates propagate across queries
    const typePolicies = {
        // Benefit IDs are stable only inside their owning plan.
        PlanBenefit: PLAN_BENEFIT_TYPE_POLICY,
        Query: {
            fields: {
                // GET_FOLDER_CONTENT - keyed by folderId, isTrash, child-message visibility flags,
                // and respectManualOrder (Board's rank-ordered pages must never merge with the
                // date-ordered pages other views fetch for the same folder — different order,
                // different cursor semantics).
                getFolderContent: {
                    keyArgs: buildVisibilityAwareKeyArgs(['folderId', 'isTrash', 'respectManualOrder']),
                    merge(existing, incoming, { readField, canRead, args }) {
                        const queryFolderId = args?.data?.folderId;

                        if (!existing) return incoming;
                        if (!incoming) return existing;

                        // Get recently moved messages for this folder
                        const recentlyMoved = typeof window !== 'undefined'
                            ? window.__recentlyMovedMessages?.get(queryFolderId) || new Set()
                            : new Set();

                        // Merge the messages arrays, deduplicating by ID
                        const existingMessages = existing?.messages?.messages || [];
                        const incomingMessages = incoming?.messages?.messages || [];

                        // Extract message ID from a message (handles both normalized refs and direct objects)
                        // Note: __ref format is 'Message:{"id":"xxx"}' so we need to parse the JSON
                        const getMessageId = (msg) => {
                            if (!msg) return null;
                            if (msg.__ref) {
                                const jsonPart = msg.__ref.replace('Message:', '');
                                try {
                                    // Parse the JSON to extract the actual id
                                    const parsed = JSON.parse(jsonPart);
                                    return parsed.id;
                                } catch (e) {
                                    // Fallback if it's not JSON (simple format like 'Message:xxx')
                                    return jsonPart;
                                }
                            }
                            if (msg.id) return msg.id;
                            return null;
                        };

                        // Helper to check if a message should be EXCLUDED
                        // We use exclusion logic to be conservative - only exclude what we're sure should be excluded
                        const shouldExcludeMessage = (msg) => {
                            const msgId = getMessageId(msg);
                            if (!msgId) return true; // Exclude invalid messages

                            // Exclude temp/optimistic messages
                            if (msgId.startsWith('temp-')) {
                                return true;
                            }

                            // Exclude if recently moved FROM this folder
                            if (recentlyMoved.has(msgId)) {
                                return true;
                            }

                            // For normalized refs, check if the message was evicted
                            if (msg.__ref) {
                                try {
                                    if (!canRead(msg)) {
                                        return true; // Message was evicted from cache
                                    }
                                } catch {
                                    // If canRead throws, message might be partially evicted
                                    // Don't exclude - better to include stale data than lose messages
                                    return false;
                                }

                                // Check if message's folder matches query folder
                                if (queryFolderId) {
                                    try {
                                        const msgFolder = readField('folder', msg);
                                        const msgFolderId = msgFolder ? readField('id', msgFolder) : null;
                                        if (msgFolderId && msgFolderId !== queryFolderId) {
                                            return true; // Message belongs to different folder
                                        }
                                    } catch {
                                        // Can't read folder - don't exclude, let it stay
                                        return false;
                                    }
                                }
                            }

                            return false;
                        };

                        // CORE MERGE LOGIC: Always preserve all messages from both sources
                        // Use a Map for deduplication - incoming overwrites existing for same ID (fresher data)
                        const messageMap = new Map();

                        // First, keep the existing paginated pages. A message-preview
                        // write can make `canRead` temporarily report false for older
                        // normalized references even though they still belong to this
                        // folder. Filtering them here collapsed a loaded Inbox back to
                        // page one. Explicit moves remain excluded via recentlyMoved.
                        existingMessages.forEach(msg => {
                            const key = getMessageId(msg);
                            if (key && !key.startsWith('temp-') && !recentlyMoved.has(key)) {
                                messageMap.set(key, msg);
                            }
                        });

                        // Then, add/update with incoming messages (these are fresher)
                        incomingMessages.forEach(msg => {
                            if (!shouldExcludeMessage(msg)) {
                                const key = getMessageId(msg);
                                if (key) messageMap.set(key, msg);
                            }
                        });

                        const mergedMessages = Array.from(messageMap.values());

                        // Folder-content pagination requests use `messageCursor`.
                        // When that argument is present we must advance to the incoming
                        // cursor, otherwise older paging can get stuck on the stale one.
                        const isPaginationRequest = Boolean(args?.data?.messageCursor);
                        const hasPaginatedData = mergedMessages.length > incomingMessages.length;
                        const hasIncomingMessagesCursor =
                            Boolean(incoming?.messages) &&
                            Object.prototype.hasOwnProperty.call(incoming.messages, 'cursor');

                        let cursor;
                        if (isPaginationRequest) {
                            cursor = hasIncomingMessagesCursor
                                ? incoming.messages.cursor
                                : (existing.messages?.cursor ?? null);
                        } else if (hasPaginatedData) {
                            cursor = existing.messages?.cursor ?? null;
                        } else if (hasIncomingMessagesCursor) {
                            cursor = incoming.messages.cursor;
                        } else {
                            cursor = existing.messages?.cursor || null;
                        }

                        return {
                            ...existing,
                            ...incoming,
                            messages: {
                                ...existing.messages,
                                ...incoming.messages,
                                messages: mergedMessages,
                                cursor
                            }
                        };
                    }
                },
                getSharedFolderContent: {
                    keyArgs: buildVisibilityAwareKeyArgs(['folderId', 'sortBy']),
                    merge: mergeSharedFolderContent
                },
                // FIND_MESSAGES - used by HierarchyFolderContainer and AllTrashMessagesContainer
                findMessages: {
                    keyArgs: buildVisibilityAwareKeyArgs(['folderId', 'isTrash', 'sortBy', 'searchMode']),
                    merge(existing, incoming, { canRead, readField, args }) {
                        if (!existing) return incoming;
                        if (!incoming) return existing;

                        const queryFolderId = String(args?.data?.folderId || '');
                        const recentlyMoved = queryFolderId
                            ? (
                                typeof window !== 'undefined'
                                    ? window.__recentlyMovedMessages?.get(queryFolderId) || new Set()
                                    : new Set()
                            )
                            : new Set();

                        const existingMessages = existing?.messages || [];
                        const incomingMessages = incoming?.messages || [];

                        // Extract message ID helper
                        // Note: __ref format is 'Message:{"id":"xxx"}' so we need to parse the JSON
                        const getMessageId = (msg) => {
                            if (!msg) return null;
                            if (msg.__ref) {
                                const jsonPart = msg.__ref.replace('Message:', '');
                                try {
                                    // Parse the JSON to extract the actual id
                                    const parsed = JSON.parse(jsonPart);
                                    return parsed.id;
                                } catch (e) {
                                    // Fallback if it's not JSON (simple format like 'Message:xxx')
                                    return jsonPart;
                                }
                            }
                            if (msg.id) return msg.id;
                            return null;
                        };

                        // Helper to check if message should be included
                        // Filters out: null messages, temp messages, and dangling references
                        const shouldIncludeMessage = (msg) => {
                            if (!msg) return false;
                            const id = getMessageId(msg);
                            if (!id) return false;
                            // Filter out optimistic/temp messages
                            if (id.startsWith('temp-')) return false;
                            // Exclude messages recently moved out of this folder.
                            if (recentlyMoved.has(id)) return false;
                            // Filter out dangling references (evicted entities)
                            if (msg.__ref && !canRead(msg)) return false;
                            return true;
                        };

                        // Merge all messages, deduplicating by ID
                        const messageMap = new Map();

                        // Add existing first (preserve paginated messages)
                        existingMessages.forEach(msg => {
                            if (shouldIncludeMessage(msg)) {
                                messageMap.set(getMessageId(msg), msg);
                            }
                        });

                        // Add/update with incoming (fresher data)
                        incomingMessages.forEach(msg => {
                            if (shouldIncludeMessage(msg)) {
                                messageMap.set(getMessageId(msg), msg);
                            }
                        });

                        const mergedMessages = Array.from(messageMap.values());
                        const requestedSearchMode = String(args?.data?.searchMode || '').toUpperCase();
                        const shouldPreserveServerOrder = requestedSearchMode === 'ATLAS_RELEVANCE';

                        if (!shouldPreserveServerOrder) {
                            // Keep legacy recency ordering for classic/recent modes.
                            const sortBy = args?.data?.sortBy || 'createdAt';
                            mergedMessages.sort((a, b) => {
                                const aDate = readField(sortBy, a) || readField('createdAt', a);
                                const bDate = readField(sortBy, b) || readField('createdAt', b);
                                return new Date(bDate) - new Date(aDate);
                            });
                        }

                        // Pagination requests must advance the cursor to the newly returned page.
                        // Non-pagination refreshes should preserve the existing cursor if we've
                        // already accumulated more data than the incoming snapshot.
                        const isPaginationRequest = Boolean(args?.data?.cursor);
                        const hasIncomingCursor = Object.prototype.hasOwnProperty.call(incoming, 'cursor');
                        const cursor = isPaginationRequest
                            ? (hasIncomingCursor ? incoming.cursor : (existing.cursor ?? null))
                            : (
                                mergedMessages.length > incomingMessages.length
                                    ? existing.cursor
                                    : (hasIncomingCursor ? incoming.cursor : (existing.cursor ?? null))
                            );

                        return {
                            ...incoming,
                            messages: mergedMessages,
                            cursor
                        };
                    }
                },
                // FILTER_MESSAGES - keyed by folderId, type array, and isTrash
                filterMessages: {
                    keyArgs: buildVisibilityAwareKeyArgs(MESSAGE_FILTER_CACHE_FIELDS),
                    merge(existing, incoming, { canRead, readField, args }) {
                        if (!existing) return incoming;
                        if (!incoming) return existing;

                        const queryFolderId = String(args?.data?.folderId || '');
                        const recentlyMoved = queryFolderId
                            ? (
                                typeof window !== 'undefined'
                                    ? window.__recentlyMovedMessages?.get(queryFolderId) || new Set()
                                    : new Set()
                            )
                            : new Set();

                        const existingMessages = existing?.messages || [];
                        const incomingMessages = incoming?.messages || [];

                        // Extract message ID helper
                        // Note: __ref format is 'Message:{"id":"xxx"}' so we need to parse the JSON
                        const getMessageId = (msg) => {
                            if (!msg) return null;
                            if (msg.__ref) {
                                const jsonPart = msg.__ref.replace('Message:', '');
                                try {
                                    // Parse the JSON to extract the actual id
                                    const parsed = JSON.parse(jsonPart);
                                    return parsed.id;
                                } catch (e) {
                                    // Fallback if it's not JSON (simple format like 'Message:xxx')
                                    return jsonPart;
                                }
                            }
                            if (msg.id) return msg.id;
                            return null;
                        };

                        // Helper to check if message should be included
                        // Filters out: null messages, temp messages, and dangling references
                        const shouldIncludeMessage = (msg) => {
                            if (!msg) return false;
                            const id = getMessageId(msg);
                            if (!id) return false;
                            // Filter out optimistic/temp messages
                            if (id.startsWith('temp-')) return false;
                            // Exclude messages recently moved out of this folder.
                            if (recentlyMoved.has(id)) return false;
                            // Drop never-viewed rows retained by an older History cache.
                            if (args?.data?.sortBy === 'lastViewedAt' && !readField('lastViewedAt', msg)) return false;
                            // Filter out dangling references (evicted entities)
                            if (msg.__ref && !canRead(msg)) return false;
                            return true;
                        };

                        // Debug logging for All view merges
                        const isAllView = args?.data?.folderId === '';
                        if (process.env.NODE_ENV === 'development' && isAllView) {
                            console.log('🔀 FILTER_MESSAGES merge (All view):', {
                                existingCount: existingMessages.length,
                                incomingCount: incomingMessages.length,
                                existingFirst3: existingMessages.slice(0, 3).map(m => getMessageId(m)),
                                incomingFirst3: incomingMessages.slice(0, 3).map(m => getMessageId(m))
                            });
                        }

                        // Merge all messages, deduplicating by ID
                        const messageMap = new Map();

                        // Add existing first (preserve paginated messages)
                        existingMessages.forEach(msg => {
                            if (shouldIncludeMessage(msg)) {
                                messageMap.set(getMessageId(msg), msg);
                            }
                        });

                        // Add/update with incoming (fresher data)
                        incomingMessages.forEach(msg => {
                            if (shouldIncludeMessage(msg)) {
                                messageMap.set(getMessageId(msg), msg);
                            }
                        });

                        let mergedMessages = Array.from(messageMap.values());
                        const requestedSearchMode = String(args?.data?.searchMode || '').toUpperCase();
                        const shouldPreserveServerOrder = requestedSearchMode === 'ATLAS_RELEVANCE';

                        if (process.env.NODE_ENV === 'development' && isAllView) {
                            console.log('🔀 FILTER_MESSAGES merge result:', {
                                mergedCount: mergedMessages.length,
                                mergedFirst3: mergedMessages.slice(0, 3).map(m => getMessageId(m))
                            });
                        }

                        if (!shouldPreserveServerOrder) {
                            // Keep legacy recency ordering for classic/recent modes.
                            const sortBy = args?.data?.sortBy || 'createdAt';
                            mergedMessages.sort((a, b) => {
                                const aDate = readField(sortBy, a) || readField('createdAt', a);
                                const bDate = readField(sortBy, b) || readField('createdAt', b);
                                return new Date(bDate) - new Date(aDate);
                            });
                        }

                        // Pagination requests must advance to the newly returned cursor.
                        // Non-pagination refreshes can preserve the existing cursor if we
                        // already accumulated more messages than the incoming snapshot.
                        const isPaginationRequest = Boolean(args?.data?.cursor);
                        const hasIncomingCursor = Object.prototype.hasOwnProperty.call(incoming, 'cursor');
                        const cursor = isPaginationRequest
                            ? (hasIncomingCursor ? incoming.cursor : (existing.cursor ?? null))
                            : (
                                mergedMessages.length > incomingMessages.length
                                    ? existing.cursor
                                    : (hasIncomingCursor ? incoming.cursor : (existing.cursor ?? null))
                            );

                        return {
                            ...incoming,
                            messages: mergedMessages,
                            cursor
                        };
                    }
                },
                // SEARCH_MESSAGES - keyed by search parameters
                searchMessages: {
                    keyArgs: buildVisibilityAwareKeyArgs(MESSAGE_FILTER_CACHE_FIELDS)
                },
                // Starred and Checkbox messages - no key args (global)
                getStarredMessagesGlobal: {
                    keyArgs: false,
                    merge(existing, incoming) {
                        return incoming; // Always use server response
                    }
                },
                getCheckboxMessagesGlobal: {
                    keyArgs: false,
                    merge(existing, incoming) {
                        return incoming; // Always use server response
                    }
                }
            }
        },
        // Message type - normalized by ID
        Message: {
            keyFields: ['id'],
            fields: {
                // Ensure folder reference is properly merged
                folder: {
                    merge(existing, incoming) {
                        return incoming || existing;
                    }
                },
                image: {
                    merge(existing, incoming, { mergeObjects }) {
                        if (incoming == null) {
                            return incoming;
                        }
                        if (!existing) {
                            return incoming;
                        }
                        return mergeObjects(existing, incoming);
                    }
                },
                file: {
                    merge(existing, incoming, { mergeObjects }) {
                        if (incoming == null) {
                            return incoming;
                        }
                        if (!existing) {
                            return incoming;
                        }
                        return mergeObjects(existing, incoming);
                    }
                },
                metadata: {
                    merge(existing, incoming, { mergeObjects }) {
                        if (incoming == null) {
                            return incoming;
                        }
                        if (!existing) {
                            return incoming;
                        }
                        return mergeObjects(existing, incoming);
                    }
                },
                urlMeta: {
                    merge(existing, incoming, { mergeObjects }) {
                        if (incoming == null) {
                            return incoming;
                        }

                        if (!Array.isArray(existing) || !Array.isArray(incoming)) {
                            return incoming;
                        }

                        const buildIdentity = (meta, index) => {
                            if (!meta) {
                                return `index:${index}`;
                            }

                            return (
                                meta.public_id ||
                                meta.url ||
                                meta.media_url ||
                                meta.media_hls_url ||
                                meta.image_cdn_url ||
                                meta.image ||
                                `index:${index}`
                            );
                        };

                        const existingByIdentity = new Map();
                        existing.forEach((meta, index) => {
                            existingByIdentity.set(buildIdentity(meta, index), meta);
                        });

                        return incoming.map((meta, index) => {
                            const existingMeta =
                                existingByIdentity.get(buildIdentity(meta, index)) ||
                                existing[index];

                            if (existingMeta && meta && typeof meta === 'object') {
                                return mergeObjects(existingMeta, meta);
                            }

                            return meta ?? existingMeta;
                        });
                    }
                }
            }
        },
        // Folder type - normalized by ID
        Folder: {
            keyFields: ['id']
        },
        // User type - normalized by ID
        User: {
            keyFields: ['id']
        },
        // Sections and entries are value objects embedded in an Artifact, not
        // globally addressable resources. Their IDs are only unique within that
        // artifact (for example, nearly every workout has an `exercises`
        // section and a `step-1` entry). Normalizing them by those IDs lets a
        // later message overwrite an earlier message's artifact content.
        ArtifactSection: {
            keyFields: false
        },
        ArtifactEntry: {
            keyFields: false
        }
    };

    const cache = new InMemoryCache({ 
        fragmentMatcher,
        typePolicies
    });

    const client = new ApolloClient({
        link: errorLink.concat(link),
        cache
    });

    return client;
};

const reconnectSubscriptionsForAuthToken = token => {
    // console.log('do you chagne me');
    // console.log(wsLink.subscriptionClient.connectionParams.authToken)
    // if (wsLink.subscriptionClient.connectionParams.authToken === token) {
    // 	console.log('ovo je token', token);
    // 	return;
    // }
    // console.log('I am here!!!!')

    // wsLink.subscriptionClient.connectionParams.authToken = token;
    // console.log(wsLink.subscriptionClient.connectionParams.authToken);

    if (wsLink && wsLink.subscriptionClient) {
        // Refresh active subscriptions; logout cancels sockets and retry timers.
        wsLink.subscriptionClient.updateAuthToken(token);
    }
    // console.log('This is a close() function',wsLink.subscriptionClient.close)
    // wsLink.subscriptionClient.connect();
};

subscribeAuthTokenChanges(reconnectSubscriptionsForAuthToken);
// Keep the old entry point for callers outside this module.
export const changeSubscriptionToken = setAuthToken;

function App() {
    const {t: lnT} = useTranslation();

    const [client, setClient] = useState(null);
    const [showCookieBannerPreview, setShowCookieBannerPreview] = useState(() => {
        if (
            typeof window === 'undefined' ||
            process.env.NODE_ENV === 'production' ||
            window.location.pathname !== '/'
        ) {
            return false;
        }
        return new URLSearchParams(window.location.search).get('previewCookieBanner') === '1';
    });

    useEffect(() => {
        const suppressLogoutApolloError = event => {
            const error = event?.error || event?.reason;
            const message = error?.message;
            const isLogoutInProgress = typeof window !== 'undefined' && window.__logoutInProgress;
            const isApolloLogoutRaceError =
                typeof message === 'string' &&
                message.includes('Cannot read properties of undefined') &&
                (message.includes("reading 'data'") || message.includes('toQueryResult'));

            if (isLogoutInProgress && isApolloLogoutRaceError) {
                if (typeof event.preventDefault === 'function') {
                    event.preventDefault();
                }
                return false;
            }
            return undefined;
        };

        window.addEventListener('error', suppressLogoutApolloError);
        window.addEventListener('unhandledrejection', suppressLogoutApolloError);

        return () => {
            window.removeEventListener('error', suppressLogoutApolloError);
            window.removeEventListener('unhandledrejection', suppressLogoutApolloError);
        };
    }, []);

    useEffect(() => {
        let currentlyPlayingVideo = null;

        const handleVideoPlay = event => {
            const nextVideo = event.target;
            if (!(nextVideo instanceof HTMLVideoElement)) {
                return;
            }

            // Only one video plays at a time, anywhere on the page.
            if (currentlyPlayingVideo && currentlyPlayingVideo !== nextVideo && !currentlyPlayingVideo.paused) {
                currentlyPlayingVideo.pause();
            }

            currentlyPlayingVideo = nextVideo;
        };

        const handleVideoPause = event => {
            if (event.target === currentlyPlayingVideo) {
                currentlyPlayingVideo = null;
            }
        };

        document.addEventListener('play', handleVideoPlay, true);
        document.addEventListener('pause', handleVideoPause, true);

        return () => {
            document.removeEventListener('play', handleVideoPlay, true);
            document.removeEventListener('pause', handleVideoPause, true);
        };
    }, []);

    useEffect(() => {
        const handleReloadShortcut = event => {
            const isReloadShortcut = (event.metaKey || event.ctrlKey) && event.key?.toLowerCase() === 'r';
            if (!isReloadShortcut) {
                return;
            }

            event.preventDefault();
            window.location.reload();
        };

        window.addEventListener('keydown', handleReloadShortcut, true);
        return () => {
            window.removeEventListener('keydown', handleReloadShortcut, true);
        };
    }, []);

    useEffect(() => {
        const preventFileNavigation = event => {
            event.preventDefault();
        };

        window.addEventListener('dragover', preventFileNavigation, false);
        window.addEventListener('drop', preventFileNavigation, false);

        return () => {
            window.removeEventListener('dragover', preventFileNavigation, false);
            window.removeEventListener('drop', preventFileNavigation, false);
        };
    }, []);

    useEffect(() => {
        return scheduleDesktopUpdateCheck();
    }, []);

    useEffect(() => {
        return registerDesktopUpdateMenuListener();
    }, []);

    useEffect(() => {
        let isMounted = true;

        const setupClient = async () => {
            const initializedClient = await initializeApolloClient();
            if (isMounted) {
                setClient(initializedClient);
            }
        };

        setupClient();

        return () => {
            isMounted = false;
        };
    }, []);

    if (!client) {
        return <div style={{ padding: 24, color: '#fff' }}>{lnT('common:loadingWorkspace')}</div>;
    }

    return (
        <ApolloProvider client={client}>
            <DndProvider backend={HTML5Backend}>
            <GraphqlErrorBoundary>
                <Routes />
                <PseudoPreviewControl />
                {showCookieBannerPreview && (
                    <CookieConsent
                        location="none"
                        buttonText={lnT('common:acceptAll')}
                        declineButtonText={lnT('common:rejectAll')}
                        cookieName="luckynote_cookie_banner_preview"
                        enableDeclineButton
                        flipButtons
                        buttonWrapperClasses="cookie-banner-preview-actions"
                        containerClasses="cookie-banner-preview"
                        buttonStyle={{
                            borderRadius: 8,
                            background: '#2b88d9',
                            border: '1px solid #2b88d9',
                            color: 'white',
                            minWidth: '104px',
                            minHeight: '36px',
                            padding: '7px 14px',
                            fontSize: '13px',
                            fontWeight: '500',
                            margin: 0
                        }}
                        declineButtonStyle={{
                            borderRadius: 8,
                            background: 'transparent',
                            border: '1px solid #d9dde1',
                            color: '#6b7280',
                            minWidth: '104px',
                            minHeight: '36px',
                            padding: '7px 14px',
                            fontSize: '13px',
                            fontWeight: '500',
                            margin: 0
                        }}
                        style={{
                            background: 'white',
                            border: '1px solid #dce4ec',
                            borderRadius: '12px',
                            padding: '16px',
                            position: 'fixed',
                            bottom: '20px',
                            right: '20px',
                            width: '340px',
                            alignItems: 'stretch',
                            zIndex: 9999,
                            flexDirection: 'column',
                            gap: '12px',
                            boxShadow: '0 8px 24px rgba(26, 39, 56, 0.12)'
                        }}
                        contentStyle={{
                            color: '#4A5465',
                            fontSize: '13px',
                            margin: 0,
                            lineHeight: '1.5',
                            maxWidth: 'none',
                            flex: 'none'
                        }}
                        onAccept={() => setShowCookieBannerPreview(false)}
                        onDecline={() => setShowCookieBannerPreview(false)}
                    >{lnT('common:weUseCookiesToImproveYourExperience')}{' '}
                        <a href="/privacy#tracking-cookies">{lnT('common:cookiePolicy')}</a>.
                    </CookieConsent>
                )}
                {/* PWA install prompt intentionally disabled */}
                <ReactTooltip
                    effect="solid"
                    place="top"
                    className="global-tooltip"
                    delayShow={300}
                    html={true}
                />
            </GraphqlErrorBoundary>
            </DndProvider>
        </ApolloProvider>
    );
}

export default App;

/*
 * Disabled cookie banner — retained here deliberately for a future consent
 * rollout. To restore it, uncomment the imports, render <CookieConsentBanner
 * onClose={() => setShowCookieBanner(false)} /> below <Routes />, and add the
 * associated CSS from index.scss. The old cookie storage helpers live in
 * utils/storage.js.
 *
 * import CookieConsent from 'react-cookie-consent';
 * import { storage } from './utils/storage';
 * const [showCookieBanner, setShowCookieBanner] = useState(true);
 *
 * // Inside GraphqlErrorBoundary, immediately after <Routes />:
 * {showCookieBanner && <CookieConsentBanner onClose={() => setShowCookieBanner(false)} />}
 *
 * const CookieConsentBanner = ({ onClose }) => (
 *   <CookieConsent
 *     location="none"
 *     buttonText="Accept all"
 *     declineButtonText="Reject all"
 *     cookieName="luckynote_cookie"
 *     enableDeclineButton
 *     flipButtons
 *     buttonWrapperClasses="cookie-banner-actions"
 *     buttonStyle={{ borderRadius: 8, background: '#2b88d9', border: '1px solid #2b88d9', color: 'white', minWidth: '104px', minHeight: '36px', padding: '7px 14px', fontSize: '13px', fontWeight: '500', margin: 0 }}
 *     declineButtonStyle={{ borderRadius: 8, background: 'transparent', border: '1px solid #d9dde1', color: '#6b7280', minWidth: '104px', minHeight: '36px', padding: '7px 14px', fontSize: '13px', fontWeight: '500', margin: 0 }}
 *     containerClasses="cookie-banner-container"
 *     contentClasses="cookie-banner-content"
 *     style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: '12px', padding: '16px', position: 'fixed', bottom: '20px', right: '20px', width: '340px', alignItems: 'stretch', zIndex: 9999, flexDirection: 'column', gap: '12px' }}
 *     contentStyle={{ color: '#4A5465', fontSize: '13px', margin: 0, lineHeight: '1.5', maxWidth: 'none', flex: 'none' }}
 *     expires={150}
 *     onAccept={async () => { await storage.setCookieConsent(true); onClose(); }}
 *     onDecline={() => onClose()}
 *   >
 *     We use cookies to improve your experience and understand how Luckynote is used. Read our{' '}
 *     <a href="/privacy#tracking-cookies">cookie policy</a>.
 *   </CookieConsent>
 * );
 */
